import React from 'react'
import {
  Box,
  Grid,
  Typography,
  Skeleton,
  TextField,
  Select,
  MenuItem,
  Button,
  FormControl,
  InputLabel,
  Paper,
  Chip,
  OutlinedInput,
  Alert,
  Link,
} from '@mui/material'
import { getSeverityColor, getSeverityTextColor, SeverityMap } from './_helpers'
import useDashboardListFilters from '../../../util/useDashboardListFilters'
import { useDashboardFilterOptions } from '../../../queries/hooks/useDashboardFilterOptions'
import { useDashboardAlertTotals } from '../../../queries/hooks/useDashboardAlertTotals'
import { useDashboardNoDateOfServiceCount } from '../../../queries/hooks/useDashboardNoDateOfServiceCount'
import {
  getSelectedRangeFromCalendarDates,
  getDashboardScopeFilters,
  getValidDashboardSeverity,
} from '../../../queries/hooks/dashboardHookUtils'
import {
  formatLocalDate,
  getDashboardTimezone,
  getDateHourInTimezone,
  parseIsoToMs,
} from '../../../util/dateUtils'

const SeverityButton = ({
  severity,
  count,
  onClick,
  disabled,
  selectedSeverity,
  isLoading,
}: {
  severity: number
  count: number
  onClick: () => void
  disabled: boolean
  selectedSeverity: number | null
  isLoading?: boolean
}) => {
  let bgColor = ''
  let textColor = ''
  let hoverColor = ''

  switch (severity) {
    case 1:
      bgColor =
        selectedSeverity === severity
          ? getSeverityColor(severity, 1)
          : getSeverityColor(severity, 0.4)
      textColor = selectedSeverity === severity ? 'white' : getSeverityTextColor(severity)
      hoverColor = getSeverityColor(severity, 0.6)
      break
    case 2:
      bgColor =
        selectedSeverity === severity
          ? getSeverityColor(severity, 1)
          : getSeverityColor(severity, 0.4)
      textColor = selectedSeverity === severity ? 'white' : getSeverityTextColor(severity)
      hoverColor = getSeverityColor(severity, 0.6)
      break
    case 3:
      bgColor =
        selectedSeverity === severity
          ? getSeverityColor(severity, 1)
          : getSeverityColor(severity, 0.4)
      textColor = selectedSeverity === severity ? 'white' : getSeverityTextColor(severity, true)
      hoverColor = getSeverityColor(severity, 0.6)
      break
  }

  const label = SeverityMap[severity as keyof typeof SeverityMap]

  return (
    <Button
      fullWidth
      data-qa-id={`filters-severity-${label}-button`}
      variant={selectedSeverity === severity ? 'contained' : 'outlined'}
      sx={{
        p: 2,
        backgroundColor: bgColor,
        color: textColor,
        '&:hover': {
          backgroundColor: hoverColor,
        },
        textTransform: 'none',
        height: '100%',
      }}
      onClick={onClick}
      disabled={disabled}
    >
      <Box>
        <Typography variant="body1" fontWeight={'medium'}>
          {label.charAt(0).toUpperCase() + label.slice(1)}
        </Typography>
        {isLoading ? (
          <Skeleton
            variant="text"
            width={32}
            height={22}
            sx={{
              mx: 'auto',
              bgcolor: selectedSeverity === severity ? 'rgba(255, 255, 255, 0.45)' : undefined,
            }}
          />
        ) : (
          <Typography variant="body2">{count}</Typography>
        )}
      </Box>
    </Button>
  )
}

/**
 * Opens the most recent month of records with no date of service, ending on the
 * newest such audit. Same setMonth walk as the default dashboard range.
 */
const snapRangeToRecentMonth = (lastAuditedAt?: string) => {
  const toMs = parseIsoToMs(lastAuditedAt)
  if (toMs === undefined) return {}
  const { date: toDate } = getDateHourInTimezone(toMs, getDashboardTimezone())
  if (!toDate) return {}

  const [year, month, day] = toDate.split('-').map(Number)
  const from = new Date(year, month - 1, day)
  from.setMonth(from.getMonth() - 1)
  return withSelectedRange(formatLocalDate(from), toDate)
}

const noDateOfServiceRecordLabel = (total: number) =>
  total === 1 ? '1 record has no date of service' : `${total} records have no date of service`

/**
 * Pairs a calendar range with the instants the detail views label it by, so a
 * range written outside the From/To inputs cannot leave that label behind.
 */
const withSelectedRange = (fromDate: string, toDate: string) => ({
  fromDate,
  toDate,
  selectedDateRange: getSelectedRangeFromCalendarDates(fromDate, toDate, getDashboardTimezone()),
})

/**
 * Reports the records the current view cannot show, and offers the one view
 * that can: every record has an audited date to range and sort by, so the
 * audited view is where the ones missing a date of service stay reachable.
 */
const NoDateOfServiceNotice: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const { data, isLoading, shown, isShownLoading } = useDashboardNoDateOfServiceCount()
  const total = data?.total ?? 0

  if (filters.noDateOfService) {
    const showingLabel =
      !isShownLoading && shown !== undefined ? ` Showing ${shown}.` : ''
    const totalLabel =
      isLoading && !data ? 'Records with no date of service' : noDateOfServiceRecordLabel(total)

    return (
      <Alert
        severity="info"
        sx={{
          mt: 2,
          alignItems: 'center',
          '& .MuiAlert-action': { pt: 0, alignItems: 'center' },
        }}
        data-qa-id="filters-no-date-of-service-applied"
        action={
          <Button
            color="primary"
            variant="text"
            size="small"
            onClick={() =>
              updateFilters({
                noDateOfService: undefined,
                // The notice that opens this view only renders on the date of
                // service, so that is the mode being returned to.
                dateMode: 'dateOfService',
                ...(filters.priorFromDate && filters.priorToDate
                  ? withSelectedRange(filters.priorFromDate, filters.priorToDate)
                  : {}),
                priorFromDate: undefined,
                priorToDate: undefined,
                graphGranularityDateRange: null,
                zoomHistory: [],
              })
            }
            data-qa-id="filters-no-date-of-service-clear"
          >
            Clear
          </Button>
        }
      >
        {`${totalLabel}.${showingLabel}`}
      </Alert>
    )
  }

  if (filters.dateMode !== 'dateOfService' || isLoading || total === 0) return null

  return (
    <Alert
      severity="info"
      sx={{ mt: 2, alignItems: 'center' }}
      data-qa-id="filters-no-date-of-service-notice"
    >
      <Link
        component="button"
        type="button"
        underline="always"
        onClick={() =>
          updateFilters({
            dateMode: undefined,
            noDateOfService: true,
            ...snapRangeToRecentMonth(data?.lastAuditedAt),
            priorFromDate: filters.fromDate,
            priorToDate: filters.toDate,
            graphGranularityDateRange: null,
            zoomHistory: [],
          })
        }
      >
        {`${noDateOfServiceRecordLabel(total)}. View the newest of them.`}
      </Link>
    </Alert>
  )
}

export const DashboardControls: React.FC = () => {
  const { filters, updateFilters } = useDashboardListFilters()
  const { alertTotals: alertCounts, isLoading: isLoadingAlertCounts } = useDashboardAlertTotals()
  const { data: filterOptions, isFetching: isFetchingDashboardFilterOptions } =
    useDashboardFilterOptions()
  const {
    fromDate = '',
    toDate = '',
    program = '',
    location = '',
    measures = [],
  } = filters
  const scopeFilters = getDashboardScopeFilters(filters)
  const validatedSeverity = getValidDashboardSeverity(filters.selectedSeverity)
  const clinician = scopeFilters.clinicianEmail ?? ''
  const patient = scopeFilters.patientMrn ?? ''
  const selectedSeverity = validatedSeverity === undefined ? null : (validatedSeverity as 1 | 2 | 3)

  return (
    <Paper elevation={0} sx={{ mb: 4, p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h6" gutterBottom>
          Date Range
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel id="date-mode-label">Date type</InputLabel>
              <Select
                labelId="date-mode-label"
                id="dateMode"
                data-qa-id="filters-date-mode-select"
                value={filters.dateMode ?? 'dateAudited'}
                label="Date type"
                onChange={(e) => {
                  updateFilters({
                    dateMode: e.target.value === 'dateOfService' ? 'dateOfService' : undefined,
                    // Only meaningful while ranging on the audited date.
                    noDateOfService: undefined,
                    graphGranularityDateRange: null,
                    zoomHistory: [],
                  })
                }}
                disabled={isFetchingDashboardFilterOptions}
              >
                <MenuItem value="dateAudited">Date audited</MenuItem>
                <MenuItem value="dateOfService">Date of service</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="fromDate"
              label="From"
              type="date"
              value={fromDate}
              onChange={(e) => {
                const nextFromDate = e.target.value
                updateFilters({
                  fromDate: nextFromDate,
                  selectedDateRange: getSelectedRangeFromCalendarDates(
                    nextFromDate,
                    toDate,
                    getDashboardTimezone()
                  ),
                  graphGranularityDateRange: null,
                  zoomHistory: [],
                })
              }}
              InputLabelProps={{ shrink: true }}
              placeholder="mm/dd/yyyy"
              disabled={isFetchingDashboardFilterOptions}
              inputProps={{ 'data-qa-id': 'filters-from-date-input' }}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              fullWidth
              id="toDate"
              label="To"
              type="date"
              value={toDate}
              onChange={(e) => {
                const nextToDate = e.target.value
                updateFilters({
                  toDate: nextToDate,
                  selectedDateRange: getSelectedRangeFromCalendarDates(
                    fromDate,
                    nextToDate,
                    getDashboardTimezone()
                  ),
                  graphGranularityDateRange: null,
                  zoomHistory: [],
                })
              }}
              InputLabelProps={{ shrink: true }}
              placeholder="mm/dd/yyyy"
              disabled={isFetchingDashboardFilterOptions}
              inputProps={{ 'data-qa-id': 'filters-to-date-input' }}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="program-label">Program</InputLabel>
              <Select
                labelId="program-label"
                id="program"
                data-qa-id="filters-program-select"
                value={program}
                label="Program"
                onChange={(e) => updateFilters({ program: e.target.value })}
                disabled={isFetchingDashboardFilterOptions}
              >
                <MenuItem value="">
                  <em>Program</em>
                </MenuItem>
                {filterOptions?.programs.map((program, index) => (
                  <MenuItem key={index} value={program}>
                    {program}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="location-label">Location</InputLabel>
              <Select
                labelId="location-label"
                id="location"
                data-qa-id="filters-location-select"
                value={location}
                label="Location"
                onChange={(e) => updateFilters({ location: e.target.value })}
                disabled={isFetchingDashboardFilterOptions}
              >
                <MenuItem value="">
                  <em>Location</em>
                </MenuItem>
                {filterOptions?.locations.map((location, index) => (
                  <MenuItem key={index} value={location}>
                    {location}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="clinician-label">Search by Clinician</InputLabel>
              <Select
                labelId="clinician-label"
                id="clinician"
                data-qa-id="filters-clinician-select"
                value={clinician}
                label="Search by Clinician"
                onChange={(e) =>
                  updateFilters({
                    userEmail: e.target.value || undefined,
                    detailViewClinicianEmail: null,
                  })
                }
                disabled={isFetchingDashboardFilterOptions}
              >
                <MenuItem value="">
                  <em>Select Clinician</em>
                </MenuItem>
                {filterOptions?.users.map((user, index) => (
                  <MenuItem key={index} value={user.email}>
                    {user.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={6}>
            <FormControl fullWidth>
              <InputLabel id="patient-label">Search by Patient</InputLabel>
              <Select
                labelId="patient-label"
                id="patient"
                data-qa-id="filters-patient-select"
                value={patient}
                label="Search by Patient"
                onChange={(e) =>
                  updateFilters({
                    patientMrn: e.target.value || undefined,
                    detailViewPatientMrn: null,
                  })
                }
                disabled={isFetchingDashboardFilterOptions}
              >
                <MenuItem value="">
                  <em>Select Patient</em>
                </MenuItem>
                {filterOptions?.patients.map((patient, index) => (
                  <MenuItem key={index} value={patient.mrn}>
                    {patient.name} ({patient.mrn})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>
      </Box>

      <Box sx={{ mb: 3 }}>
        <FormControl fullWidth>
          <InputLabel id="measures-label">Filter by Measures</InputLabel>
          <Select
            labelId="measures-label"
            id="measures"
            data-qa-id="filters-measures-select"
            multiple
            value={measures}
            onChange={(event) => {
              const value = event.target.value
              updateFilters({
                measures: typeof value === 'string' ? value.split(',') : value,
              })
            }}
            input={<OutlinedInput id="select-multiple-measures" label="Filter by Measures" />}
            renderValue={(selected) => (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {selected.map((value) => (
                  <Chip
                    key={value}
                    label={
                      filterOptions?.measures.find((measure) => measure.id === value)?.title ??
                      value
                    }
                  />
                ))}
              </Box>
            )}
            disabled={isFetchingDashboardFilterOptions}
          >
            {filterOptions?.measures.map((measure) => (
              <MenuItem key={measure.id} value={measure.id}>
                {measure.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Box>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <SeverityButton
              severity={1}
              count={alertCounts?.low}
              onClick={() => updateFilters({ selectedSeverity: selectedSeverity === 1 ? null : 1 })}
              disabled={isFetchingDashboardFilterOptions || isLoadingAlertCounts}
              selectedSeverity={selectedSeverity}
              isLoading={isLoadingAlertCounts}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SeverityButton
              severity={2}
              count={alertCounts?.medium}
              onClick={() => updateFilters({ selectedSeverity: selectedSeverity === 2 ? null : 2 })}
              disabled={isFetchingDashboardFilterOptions || isLoadingAlertCounts}
              selectedSeverity={selectedSeverity}
              isLoading={isLoadingAlertCounts}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SeverityButton
              severity={3}
              count={alertCounts?.critical}
              onClick={() => updateFilters({ selectedSeverity: selectedSeverity === 3 ? null : 3 })}
              disabled={isFetchingDashboardFilterOptions || isLoadingAlertCounts}
              selectedSeverity={selectedSeverity}
              isLoading={isLoadingAlertCounts}
            />
          </Grid>
        </Grid>
        <NoDateOfServiceNotice />
      </Box>
    </Paper>
  )
}
